/* Copyright & Terms of Use — single legal page covering copyright, permitted
 * use, trademarks, disclaimers, third-party links and contact info. Linked
 * from every footer (Home, Systems, Services, Academy) by wrapping the
 * "© YEAR ZHELTA LLC. All rights reserved." string in a clickable link. */
import React, { useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SeoHead from '../components/SeoHead';
import { Mail, MapPin } from 'lucide-react';
import { getOrganizationSchema } from '../data/orgSchema';

export default function Copyright() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // schema.org Organization payload — surfaced via SeoHead. Keeps Google
  // happy (sitelinks, knowledge-panel hints, richer SERP cards) and gives
  // the legal page a clear "owner of record" stamp. Single source of truth
  // shared with Home.jsx so identity signals stay consistent sitewide.
  const orgJsonLd = getOrganizationSchema();

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        path="/copyright"
        title="Copyright & Terms of Use — ZHELTA"
        description="Copyright information, permitted use, trademarks, disclaimers, and contact details for ZHELTA LLC."
        jsonLd={orgJsonLd}
      />
      <Header />

      <main className="py-16 md:py-24" data-testid="copyright-page">
        <div className="zh-container max-w-[820px]">
          {/* Eyebrow */}
          <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#c9a227]">
            Legal
          </span>

          {/* Title */}
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Copyright &amp; <span className="text-[#f5a623]">Terms of Use</span>
          </h1>

          <p className="mt-6 text-[16px] text-gray-600 leading-relaxed">
            All content on <strong className="text-gray-900">zhelta.com</strong> is protected by
            applicable copyright laws. Please read these terms carefully.
          </p>
          <p className="mt-3 text-[15px] text-gray-600 leading-relaxed">
            Welcome to <strong className="text-gray-900">ZHELTA LLC</strong>. By accessing or using
            our website, applications, products, or services, you agree to the following terms
            and conditions. These terms are subject to change at any time. We encourage you to
            review this page regularly.
          </p>

          <div className="mt-12 space-y-12 text-[15px] text-gray-700 leading-[1.75]">
            {/* Copyright information */}
            <section data-testid="copyright-section-info">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Copyright Information
              </h2>
              <p>
                All content on zhelta.com and related platforms, including but not limited to
                text, graphics, logos, icons, images, videos, software, downloadable tools,
                product listings, educational material, and other intellectual property, is the
                property of ZHELTA LLC or its content suppliers, protected by applicable U.S.
                and international copyright laws.
              </p>
              <p className="mt-4">
                Unauthorized use, reproduction, modification, or distribution of this content is
                strictly prohibited unless expressly permitted in writing by ZHELTA LLC.
              </p>
            </section>

            {/* Permitted Use */}
            <section data-testid="copyright-section-use">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Permitted Use
              </h2>
              <p className="font-semibold text-gray-900">You may:</p>
              <ul className="mt-2 list-disc pl-6 space-y-1.5 marker:text-[#f5a623]">
                <li>View and use the content for personal or non-commercial purposes.</li>
                <li>
                  Share content using social media links provided, so long as such sharing does
                  not modify the content.
                </li>
                <li>Download material made explicitly available for download for informational use.</li>
              </ul>

              <p className="mt-6 font-semibold text-gray-900">You may not:</p>
              <ul className="mt-2 list-disc pl-6 space-y-1.5 marker:text-[#f5a623]">
                <li>Modify, reproduce, or create derivative works without permission.</li>
                <li>Use any trademarks or logos without express written consent.</li>
                <li>Use the website in a way that violates applicable laws.</li>
              </ul>
            </section>

            {/* Trademarks */}
            <section data-testid="copyright-section-trademarks">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Trademarks
              </h2>
              <p>
                &ldquo;ZHELTA LLC,&rdquo; &ldquo;ZHELTA YOUR LAST STOP FOR TECH SERVICES,&rdquo;
                and related logos and taglines are trademarks or registered trademarks of ZHELTA
                LLC. All other trademarks used on this site are the property of their respective
                owners.
              </p>
            </section>

            {/* Disclaimers */}
            <section data-testid="copyright-section-disclaimers">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Disclaimers and Liability
              </h2>
              <p>
                All content and services are provided &ldquo;as is&rdquo; and &ldquo;as
                available.&rdquo; We make no warranties about accuracy, completeness, or
                suitability. Your use of our platforms is at your own risk.
              </p>
              <p className="mt-4">
                ZHELTA LLC shall not be liable for any direct, indirect, incidental, consequential,
                or punitive damages arising from your use.
              </p>
            </section>

            {/* Third-Party */}
            <section data-testid="copyright-section-thirdparty">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Third-Party Links and Updates
              </h2>
              <p>
                Our website may include links to third-party websites. ZHELTA LLC does not
                endorse or assume responsibility for their content or practices.
              </p>
              <p className="mt-4">
                We reserve the right to update, change, or discontinue any part of our website or
                these terms at any time.
              </p>
            </section>

            {/* Contact */}
            <section data-testid="copyright-section-contact">
              <h2 className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-4">
                Contact Us
              </h2>
              <p>
                For questions or requests related to these terms or our copyright policy, please
                contact us at:
              </p>
              <div className="mt-5 rounded-2xl border border-gray-200 bg-[#FEFBF3] p-5 sm:p-6">
                <div className="flex items-start gap-3">
                  <Mail className="w-4 h-4 mt-1 text-[#c9a227] flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
                      Email
                    </div>
                    <a
                      href="mailto:legals@zhelta.com"
                      className="text-[15px] font-semibold text-gray-900 hover:text-[#c9a227]"
                      data-testid="copyright-contact-email"
                    >
                      legals@zhelta.com
                    </a>
                  </div>
                </div>
                <div className="mt-4 flex items-start gap-3">
                  <MapPin className="w-4 h-4 mt-1 text-[#c9a227] flex-shrink-0" />
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-500">
                      Address
                    </div>
                    <div className="text-[15px] font-semibold text-gray-900">
                      167 Madison Avenue, Suite 205 #1076
                      <br />
                      New York, NY 10016, United States
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Last updated */}
            <p className="text-[12.5px] text-gray-400 pt-6 border-t border-gray-100">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              {' · '}
              &copy; {new Date().getFullYear()} ZHELTA LLC. All rights reserved.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
